import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';

// Role hierarchy - higher index = more permissions
export const ROLE_HIERARCHY = [
  'SALESPERSON',
  'INVENTORY_EDITOR',
  'DEALERSHIP_ADMIN',
  'SYSTEM_ADMIN',
] as const;

export type UserRole = typeof ROLE_HIERARCHY[number];

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private _userSubject = new BehaviorSubject<string>('Guest');
  private _loggedInSubject = new BehaviorSubject<boolean>(false);
  private _rolesSubject = new BehaviorSubject<string[]>([]);
  isLoggedIn$ = this._loggedInSubject.asObservable();
  currentLoggedInUser$ = this._userSubject.asObservable();
  roles$ = this._rolesSubject.asObservable();
  private readonly _oidcSecurityService = inject(OidcSecurityService);
  private readonly _router = inject(Router);
  private _loggedInUser = 'Guest';
  private _isAuthenticated = false;
  private _userRoles: string[] = [];


  constructor() {
    console.log("AuthService constructor called...")
    this._oidcSecurityService.getAccessToken().subscribe(token => {
      console.log("Raw ID Token:", token);
    });

    this._oidcSecurityService.checkAuth().subscribe(({ isAuthenticated, userData}) => {
      console.log("checkAuth result - isAuthenticated:", isAuthenticated);
      console.log("current user: ", userData?.name);
      console.log('userData', userData);

      this._isAuthenticated = isAuthenticated;
      this._loggedInSubject.next(isAuthenticated);
      
      if (isAuthenticated && userData) {
        this._loggedInUser = userData.name;
        this._userSubject.next(userData.name);

        combineLatest([
          this._oidcSecurityService.getPayloadFromAccessToken()
        ]).subscribe(([payload]) => {
          this._userRoles = this.extractRoles(payload);
          this._rolesSubject.next(this._userRoles);
          console.log("user roles: ", this._userRoles);
        });

        // Redirect to the saved URL after login
        const returnUrl = sessionStorage.getItem('returnUrl');
        if (returnUrl) {
          sessionStorage.removeItem('returnUrl');
          // Only allow relative paths to prevent open redirect attacks
          if (returnUrl.startsWith('/')) {
            this._router.navigateByUrl(returnUrl);
          }
        }
      }
    });
  }

  private extractRoles(token: any): string[] {
    const roles: string[] = [];

    console.log('token', token);
    
    // Keycloak realm roles
    if (token?.realm_access?.roles) {
      roles.push(...token.realm_access.roles);
    }
    
    // Keycloak resource/client roles (check for dms-website client)
    if (token?.resource_access?.['dms-website']?.roles) {
      roles.push(...token.resource_access['dms-website'].roles);
    }
    
    // Some OIDC providers put roles directly in a 'roles' claim
    if (token?.roles && Array.isArray(token.roles)) {
      roles.push(...token.roles);
    }
    
    return [...new Set(roles)]; // Remove duplicates
  }

  getUserRoles(): string[] {
    return this._userRoles;
  }

  hasRole(role: string): boolean {
    return this._userRoles.includes(role);
  }

  hasRoleOrHigher(requiredRole: UserRole): boolean {
    const requiredIndex = ROLE_HIERARCHY.indexOf(requiredRole);
    console.log('required role', requiredRole);
    if (requiredIndex === -1) return false;
    
    return this._userRoles.some(userRole => {
      const userRoleIndex = ROLE_HIERARCHY.indexOf(userRole as UserRole);
      console.log('user role', userRole);
      console.log('user role index', userRoleIndex);
      return userRoleIndex >= requiredIndex;
    });
  }

  isLoggedIn() {
    return this._isAuthenticated;
  }

  loggedInUser() {
    return this._loggedInUser;
  }

  login() {
    console.log("logging in...");
    // const somePopupOptions = { width: 500, height: 500, left: 50, top: 50 };
    // this._oidcSecurityService.authorizeWithPopUp().subscribe(({ isAuthenticated, userData, accessToken, errorMessage }) => {
    //   /* ... */
    //   });
    this._oidcSecurityService.authorize();
  }

  logout() {
    console.log("logging out...")
    this._oidcSecurityService.logoff().subscribe((result) => console.log("logout result", result));
  }

  getToken(): Observable<string> {
    return this._oidcSecurityService.getAccessToken();
  }
}
